import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { EyeIcon } from 'lucide-react';
import { HistoryEntryRow } from '../../history/HistoryEntryRow';
import { PillButton } from '../../ui/PillButton';
import { CutoutFrame } from '../CutoutFrame';
import type { HistoryEntry, HistoryKind, ModelSnapshot } from '../../../types/history';
const snapshot: ModelSnapshot = {
  quaternion: [0, 0, 0, 1],
  lift: 0,
  position: [0, 0, 0],
  protect: [],
  touchpoints: 0,
  hasSupports: false
};
const now = Date.now();
const make = (partId: string, i: number, kind: HistoryKind, title: string, description: string): HistoryEntry => ({
  id: `${partId}-${i}`,
  partId,
  kind,
  title,
  description,
  time: now - i * 120000,
  thumbnail: '',
  snapshot
});
const initial: Record<string, HistoryEntry[]> = {
  main: [make('main', 0, 'supports', 'Generated supports (Auto)', 'Added 86 touchpoints, avoided 12 on protected areas'), make('main', 1, 'protect', 'Protected surfaces', 'AI protected 2 cog areas'), make('main', 2, 'orient', 'Auto-oriented model', 'Protected surfaces turned away from supports'), make('main', 3, 'import', 'Imported model', 'Mini Chassis Kit.glb')],
  copy: [make('copy', 0, 'move', 'Moved model', 'Repositioned on the build platform'), make('copy', 1, 'import', 'Duplicated model', 'Copied from the original')]
};
const parts = [{
  id: 'main',
  name: 'Mini Chassis Kit'
}, {
  id: 'copy',
  name: 'Mini Chassis Kit (copy)'
}];

/** Real history rows with per-model timelines and working restore. */
export function HistoryDemo() {
  const [partId, setPartId] = useState('main');
  const [entries, setEntries] = useState(initial);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const list = entries[partId];
  const selected = list.find(e => e.id === selectedId) ?? list[0];
  const restore = (e: HistoryEntry) => {
    setEntries(prev => ({
      ...prev,
      [partId]: prev[partId].slice(prev[partId].indexOf(e))
    }));
    setSelectedId(e.id);
  };
  return <CutoutFrame label="Project list with per-model history" className="pointer-events-auto flex h-[330px] !overflow-visible bg-white" data-id="element-980" data-mp="2n6ulx94za4au">
      <ul className="w-[128px] shrink-0 overflow-hidden rounded-l-[6px] border-r border-pf-line bg-[#f2f6fb] pt-[6px]" aria-label="Models" data-id="element-981" data-mp="2ibf9mlmmcggj">
        {parts.map(p => <li key={p.id} data-id="element-982" data-mp="28ighwuvts9sp">
            <button type="button" onClick={() => {
          setPartId(p.id);
          setSelectedId(null);
        }} aria-pressed={partId === p.id} className={`flex h-[26px] w-full items-center gap-[6px] pl-[8px] pr-[4px] text-left text-[11px] text-pf-ink transition-colors duration-150 ${partId === p.id ? 'border-l-[3px] border-pf-blue bg-[#d3e3f7] pl-[5px] font-semibold' : 'hover:bg-[#e9f0f9]'}`} data-id="element-983" data-mp="28h5gv2trlg5a">
            
              <EyeIcon className="h-[11px] w-[11px] shrink-0" strokeWidth={2.4} data-id="element-984" data-mp="3ov5fmagvizqy" />
              <span className="truncate" data-id="element-985" data-mp="3jz7w5vabdd9y">{p.name}</span>
            </button>
          </li>)}
      </ul>
      <div className="flex min-w-0 flex-1 flex-col" data-id="element-986" data-mp="3rfuzxufhp6jf">
        <div className="min-h-0 flex-1 overflow-y-auto px-[8px]" data-id="element-987" data-mp="al61rodk84j">
          <h3 className="pb-[2px] pt-[8px] text-[12.5px] font-bold text-pf-ink" data-id="element-988" data-mp="52d22hqav622">Today</h3>
          <ul data-id="element-989" data-mp="54pgwm0rh0aw">
            <AnimatePresence initial={false} data-id="element-990" data-mp="a16d83i46kz2">
              {list.map((e, i) => <HistoryEntryRow key={e.id} entry={e} today selected={selected?.id === e.id} current={i === 0} isFirst={i === 0} isLast={i === list.length - 1} onSelect={() => setSelectedId(e.id)} onRestore={() => restore(e)} onSaveVersion={() => undefined} data-id="element-991" data-mp="a3aiopvnvs1g" data-mpi={i} />)}
            </AnimatePresence>
          </ul>
        </div>
        <div className="border-t border-pf-line px-[8px] py-[7px]" data-id="element-992" data-mp="xtd4io8zg1rm">
          <PillButton variant="outlineAccent" className="h-[26px] w-full text-[12px] disabled:border-pf-line disabled:text-pf-faint" disabled={!selected || selected.id === list[0].id} onClick={() => selected && restore(selected)} data-id="element-993" data-mp="12ok2dffkmc8m">
            
            {selected && selected.id === list[0].id ? 'This is the current version' : 'Restore to this version'}
          </PillButton>
        </div>
      </div>
    </CutoutFrame>;
}